/**
 * Shared numbers for the 3D device that server-rendered code can import
 * without pulling three.js into the page bundle.
 */

/** Width divided by height of the device face (2.12 by 1.38 world units). */
export const DEVICE_ASPECT = 2.12 / 1.38

export type DevicePose = {
    rotationX: number
    rotationY: number
    rotationZ: number
    depth: number
    scale: number
    idle: number
    shadow: number
}

export const clamp01 = (value: number): number => Math.min(1, Math.max(0, value))

export const smoothstep = (value: number): number => {
    const t = clamp01(value)

    return t * t * (3 - 2 * t)
}

export const lerp = (from: number, to: number, amount: number): number => from + (to - from) * amount

/** Interpolates between pose keyframes with eased segments. */
export const samplePose = (frames: Array<{ at: number } & DevicePose>, phase: number): DevicePose => {
    if (phase <= frames[0].at) return frames[0]

    for (let index = 0; index < frames.length - 1; index++) {
        const from = frames[index]
        const to = frames[index + 1]

        if (phase <= to.at) {
            const amount = smoothstep((phase - from.at) / (to.at - from.at))

            return {
                rotationX: lerp(from.rotationX, to.rotationX, amount),
                rotationY: lerp(from.rotationY, to.rotationY, amount),
                rotationZ: lerp(from.rotationZ, to.rotationZ, amount),
                depth: lerp(from.depth, to.depth, amount),
                scale: lerp(from.scale, to.scale, amount),
                idle: lerp(from.idle, to.idle, amount),
                shadow: lerp(from.shadow, to.shadow, amount)
            }
        }
    }

    return frames[frames.length - 1]
}

/** Largest device width (in pixels) that fits inside a slot. */
export const fitWidth = (rect: DOMRect, padding = 0.88, max = 620): number => {
    return Math.min(max, Math.min(rect.width, rect.height * DEVICE_ASPECT) * padding)
}
